/**
 * videoPassport.js — "Video Implementation Passport" rollup.
 *
 * Normalizes a single video ad *request* row (gam_video / ima / amazon_video /
 * springserve_ad_request) into a uniform at-a-glance model: 8 fixed rows
 * (position, stream, delivery, skippable, duration, autoplay, measurable,
 * signals) plus a pass/warn/fail verdict.
 *
 * REQUEST-TIME ONLY — this slice never reads VAST/response data. A later
 * slice can compare the declared request-time bounds against what the VAST
 * response actually delivered.
 *
 * Pure module — no React/DOM — unit-tests in node. Mirrors the project's
 * validator convention: never throw, degrade to an 'info' row or return null.
 */

const ORTB_TYPES = new Set(['amazon_video', 'springserve_ad_request']);

/** Parse a URL's query string safely. Returns null (never throws) if unparsable/missing. */
function safeParams(url) {
  if (!url) return null;
  try {
    return new URL(url).searchParams;
  } catch {
    return null;
  }
}

function get(params, key) {
  if (!params) return null;
  const v = params.get(key);
  return v === '' ? null : v;
}

function row(key, label, state, value, note) {
  return { key, label, state, value, note: note || '' };
}

// ---------------------------------------------------------------------------
// gam_video
// ---------------------------------------------------------------------------

function normalizeVpos(vpos) {
  if (!vpos) return null;
  const v = String(vpos).toLowerCase();
  if (v === 'preroll') return 'PRE-ROLL';
  if (v === 'midroll') return 'MID-ROLL';
  if (v === 'postroll') return 'POST-ROLL';
  return null;
}

function positionFromDelay(delayRaw) {
  if (delayRaw === null || delayRaw === undefined || delayRaw === '') return null;
  const d = parseInt(delayRaw, 10);
  if (Number.isNaN(d)) return null;
  if (d === 0) return 'PRE-ROLL';
  if (d > 0) return 'MID-ROLL';
  if (d === -1) return 'POST-ROLL';
  return null;
}

function buildGamVideoRows(params) {
  const vpos = get(params, 'vpos');
  const startDelayRaw = get(params, 'videoad_start_delay') ?? get(params, 'startdelay');
  const output = get(params, 'output') || '';
  const adRule = get(params, 'ad_rule');
  const vpmute = get(params, 'vpmute');
  const vpa = get(params, 'vpa');
  const descriptionUrl = get(params, 'description_url');
  const pmnd = get(params, 'pmnd');
  const pmxd = get(params, 'pmxd');
  const cmsid = get(params, 'cmsid');
  const vid = get(params, 'vid');
  const rdid = get(params, 'rdid');
  const npa = get(params, 'npa');
  const omidP = get(params, 'omid_p');
  const omidV = get(params, 'omid_v');
  const sdkApis = get(params, 'sdk_apis');
  const minAdDuration = get(params, 'min_ad_duration');
  const maxAdDuration = get(params, 'max_ad_duration');

  // 1. Position
  const vposPos = normalizeVpos(vpos);
  const delayPos = positionFromDelay(startDelayRaw);
  let posState, posValue, posNote;
  if (vposPos && delayPos) {
    if (vposPos !== delayPos) {
      posState = 'warn';
      posValue = vposPos;
      posNote = `vpos=${vpos} but startdelay=${startDelayRaw} — inconsistent`;
    } else {
      posState = 'pass';
      posValue = vposPos;
      posNote = `vpos=${vpos}`;
    }
  } else if (vposPos) {
    posState = 'pass';
    posValue = vposPos;
    posNote = `vpos=${vpos}`;
  } else if (delayPos) {
    posState = 'pass';
    posValue = delayPos;
    posNote = `startdelay=${startDelayRaw}`;
  } else {
    posState = 'info';
    posValue = '—';
    posNote = 'no vpos/startdelay on request';
  }

  // 2. Stream
  const isVmap = adRule === '1' || /vmap/i.test(output);
  const streamRow = row(
    'stream', 'Stream', 'pass',
    isVmap ? 'VMAP AD-RULES' : 'SINGLE VAST',
    isVmap ? `ad_rule=${adRule || ''} output=${output || ''}` : `output=${output || 'n/a'}`
  );

  // 3. Delivery
  const hasCms = !!(cmsid && vid);
  const deliveryRow = row(
    'delivery', 'Delivery', 'info',
    hasCms ? 'DAI / SSAI · VOD' : 'Client-side',
    hasCms ? `cmsid=${cmsid} vid=${vid}` : 'no cmsid/vid — client-stitched ad break'
  );

  // 4. Skippable
  const skippableRow = row(
    'skippable', 'Skippable', 'info',
    'Set by line item',
    'not declared on the ad request'
  );

  // 5. Duration
  let durState, durValue, durNote;
  if (pmnd || pmxd) {
    durState = 'pass';
    durValue = `${pmnd || '?'}–${pmxd || '?'}s (pod)`;
    durNote = `pmnd=${pmnd || ''} pmxd=${pmxd || ''}`;
  } else if (minAdDuration || maxAdDuration) {
    durState = 'pass';
    durValue = `${minAdDuration || '?'}–${maxAdDuration || '?'}s`;
    durNote = `min_ad_duration=${minAdDuration || ''} max_ad_duration=${maxAdDuration || ''}`;
  } else {
    durState = 'info';
    durValue = '—';
    durNote = 'no duration bounds on request';
  }

  // 6. Autoplay
  let apState, apValue, apNote;
  if (vpmute === '1') {
    apState = 'pass';
    apValue = 'Muted autoplay';
    apNote = 'vpmute=1';
  } else if (vpa === 'auto') {
    apState = 'warn';
    apValue = 'Autoplay, sound ON';
    apNote = 'vpa=auto, vpmute not set';
  } else {
    apState = 'info';
    apValue = '—';
    apNote = 'vpa/vpmute not present';
  }

  // 7. Measurable
  const sdkApisList = sdkApis ? sdkApis.split('_') : [];
  const hasOmid = !!(omidP || omidV) || sdkApisList.includes('7');
  const measurableRow = row(
    'measurable', 'Measurable', hasOmid ? 'pass' : 'warn',
    hasOmid ? 'OMID ✓' : 'OMID not declared',
    hasOmid
      ? `omid_p=${omidP || ''} omid_v=${omidV || ''} sdk_apis=${sdkApis || ''}`
      : 'no omid_p/omid_v/sdk_apis=7'
  );

  // 8. Signals
  const signalsRow = row(
    'signals', 'Signals', descriptionUrl ? 'pass' : 'fail',
    descriptionUrl ? 'description_url ✓' : 'description_url MISSING',
    `${descriptionUrl ? '' : 'required for programmatic video demand; '}rdid=${rdid ? 'present' : 'absent'} npa=${npa ?? 'absent'}`
  );

  return [
    row('position', 'Position', posState, posValue, posNote),
    streamRow,
    deliveryRow,
    skippableRow,
    row('duration', 'Duration', durState, durValue, durNote),
    row('autoplay', 'Autoplay', apState, apValue, apNote),
    measurableRow,
    signalsRow,
  ];
}

function buildFromGamVideo(rowData) {
  const params = safeParams(rowData.url);
  const rows = buildGamVideoRows(params);
  const iu = get(params, 'iu');
  return { source: 'gam_video', iu, rows };
}

// ---------------------------------------------------------------------------
// ima
// ---------------------------------------------------------------------------

function buildFromIma(rowData) {
  const expanded = (rowData.validData && rowData.validData.expanded) || {};
  const { descriptionUrl, isLive, dai, rdid, npa, iu } = expanded;

  const ssai = dai === 'dfpnonhosted';

  const rows = [
    row('position', 'Position', 'info', 'Stream (VMAP-driven)', 'per-ad position set by VMAP'),
    row('stream', 'Stream', 'pass', 'VMAP / DAI', `dai=${dai || 'n/a'}`),
    row(
      'delivery', 'Delivery', 'info',
      `${ssai ? 'DAI / SSAI' : 'Client-side DAI'} · ${isLive ? 'LIVE' : 'VOD'}`,
      `dai=${dai || 'n/a'} isLive=${!!isLive}`
    ),
    row('skippable', 'Skippable', 'info', 'Set by line item', 'not declared on the ad request'),
    row('duration', 'Duration', 'info', '—', 'not declared on a stream-registration request'),
    row('autoplay', 'Autoplay', 'info', '—', 'not declared on a stream-registration request'),
    row('measurable', 'Measurable', 'info', '—', 'OMID negotiated later'),
    row(
      'signals', 'Signals', descriptionUrl ? 'pass' : 'fail',
      descriptionUrl ? 'description_url ✓' : 'description_url MISSING',
      `${descriptionUrl ? '' : 'required for programmatic video demand; '}rdid=${rdid ? 'present' : 'absent'} npa=${npa ?? 'absent'}`
    ),
  ];

  return { source: 'ima', iu: iu || null, rows };
}

// ---------------------------------------------------------------------------
// ortb (amazon_video / springserve_ad_request)
// ---------------------------------------------------------------------------

function buildFromOrtb(rowData) {
  const videoOrtb = rowData.validData && rowData.validData.videoOrtb;
  if (!videoOrtb || !videoOrtb.isVideo || !Array.isArray(videoOrtb.imps) || !videoOrtb.imps.length) {
    return null;
  }
  const imp = videoOrtb.imps[0] || {};
  const content = videoOrtb.content;

  // 1. Position
  let posValue = imp.startdelayLabel || null;
  let posState = 'pass';
  if (!posValue) {
    posValue = positionFromDelay(imp.startdelay);
  }
  if (!posValue) {
    posState = 'info';
    posValue = '—';
  }
  const posNote = imp.startdelayLabel
    ? `startdelayLabel=${imp.startdelayLabel}`
    : (imp.startdelay !== undefined ? `startdelay=${imp.startdelay}` : 'no startdelay on request');

  // 2. Stream
  const isPod = !!(imp.podid || imp.poddur || imp.maxseq);
  const streamRow = row(
    'stream', 'Stream', 'pass',
    isPod ? 'POD' : 'SINGLE',
    isPod ? `podid=${imp.podid || ''} poddur=${imp.poddur || ''} maxseq=${imp.maxseq || ''}` : 'single ad slot'
  );

  // 3. Delivery
  const isLive = content && content.livestream === 1;
  const deliveryRow = row(
    'delivery', 'Delivery', 'info',
    isLive ? 'LIVE' : 'VOD',
    content ? `content.livestream=${content.livestream}` : 'no content object'
  );

  // 4. Skippable
  let skipState, skipValue, skipNote;
  if (imp.skip === 1) {
    skipState = 'pass';
    skipValue = `Yes${imp.skipafter ? ` (after ${imp.skipafter}s)` : ''}`;
    skipNote = `skip=1 skipmin=${imp.skipmin || ''} skipafter=${imp.skipafter || ''}`;
  } else {
    skipState = 'info';
    skipValue = 'No';
    skipNote = `skip=${imp.skip ?? 'absent'}`;
  }

  // 5. Duration
  let durState, durValue, durNote;
  if (imp.maxduration) {
    durState = 'pass';
    durValue = `${imp.minduration ?? '?'}–${imp.maxduration}s requested`;
    durNote = `minduration=${imp.minduration ?? ''} maxduration=${imp.maxduration}`;
  } else {
    durState = 'warn';
    durValue = `${imp.minduration ?? '?'}–?s requested`;
    durNote = 'no max duration bound';
  }

  // 6. Autoplay
  // inspectVideoOrtb stores playbackmethod as mapList() → [{code,label}] (not a
  // raw int), so read the first entry's code. ORTB allows several; the first is
  // the player's primary method.
  const pmArr = Array.isArray(imp.playbackmethod) ? imp.playbackmethod : [];
  const pm = pmArr.length && pmArr[0] ? pmArr[0].code : null;
  let apState, apValue, apNote;
  if (pm === 1) {
    apState = 'warn';
    apValue = 'Autoplay, sound ON';
    apNote = 'playbackmethod=1';
  } else if (pm === 2 || pm === 6) {
    apState = 'pass';
    apValue = 'Muted autoplay';
    apNote = `playbackmethod=${pm}`;
  } else if (pm === 3) {
    apState = 'pass';
    apValue = 'Click-to-play';
    apNote = 'playbackmethod=3';
  } else {
    apState = 'info';
    apValue = '—';
    apNote = `playbackmethod=${pm ?? 'absent'}`;
  }

  // 7. Measurable
  let measurableNote = imp.hasOmid ? 'hasOmid=true' : 'hasOmid=false';
  if (imp.hasVpaid) measurableNote += ' — VPAID present — deprecated';
  const measurableRow = row(
    'measurable', 'Measurable', imp.hasOmid ? 'pass' : 'warn',
    imp.hasOmid ? 'OMID ✓' : 'No OMID',
    measurableNote
  );

  // 8. Signals
  const signalsRow = row(
    'signals', 'Signals', 'pass', 'ORTB request',
    Array.isArray(imp.protocols) && imp.protocols.length
      ? `protocols=[${imp.protocols.map(p => (p && p.label) || (p && p.code) || p).join(', ')}]`
      : 'description_url is a GAM/IMA concern'
  );

  const rows = [
    row('position', 'Position', posState, posValue, posNote),
    streamRow,
    deliveryRow,
    row('skippable', 'Skippable', skipState, skipValue, skipNote),
    row('duration', 'Duration', durState, durValue, durNote),
    row('autoplay', 'Autoplay', apState, apValue, apNote),
    measurableRow,
    signalsRow,
  ];

  return { source: 'ortb', iu: null, rows };
}

// ---------------------------------------------------------------------------
// Verdict rollup
// ---------------------------------------------------------------------------

function rollupVerdict(rows) {
  const fails = rows.filter((r) => r.state === 'fail').length;
  const warns = rows.filter((r) => r.state === 'warn').length;
  if (fails > 0) {
    return { level: 'fail', label: `✕ ${fails} blocking` };
  }
  if (warns > 0) {
    return { level: 'warn', label: `▲ ${warns} to review` };
  }
  return { level: 'pass', label: '● Healthy' };
}

/**
 * Normalize a video ad request row into the "Video Implementation Passport"
 * at-a-glance model. Request-time only — never reads VAST/response data.
 *
 * @param {{ requestType?: string, type?: string, url?: string, validData?: object }} row
 * @returns {null | {
 *   source: 'gam_video'|'ima'|'ortb'|'unknown',
 *   iu: string|null,
 *   verdict: { level: 'pass'|'warn'|'fail', label: string },
 *   rows: Array<{ key: string, label: string, state: 'pass'|'warn'|'fail'|'info', value: string, note: string }>
 * }}
 */
export function buildVideoPassport(row) {
  try {
    if (!row) return null;
    const type = row.requestType || row.type;

    let built = null;
    if (type === 'gam_video') {
      built = buildFromGamVideo(row);
    } else if (type === 'ima') {
      built = buildFromIma(row);
    } else if (ORTB_TYPES.has(type)) {
      built = buildFromOrtb(row);
    } else {
      return null;
    }

    if (!built) return null;

    return {
      source: built.source,
      iu: built.iu ?? null,
      verdict: rollupVerdict(built.rows),
      rows: built.rows,
    };
  } catch {
    return null;
  }
}

export default buildVideoPassport;
